'use client';

import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { BlogArticleCard } from '@/sanity/pages/PageBlogOverviewTemplate';
import type { PortableTextBlock } from 'sanity';

const INITIAL_COUNT = 6;
const INCREMENT = 6;

function portableTextToPlain(value?: PortableTextBlock[]): string {
  if (!value?.length) return '';
  return value
    .map((block: any) => {
      if (block?._type !== 'block' || !Array.isArray(block.children)) return '';
      return block.children.map((c: any) => c?.text ?? '').join('');
    })
    .join(' ');
}

export function useArticleGrid(articles: BlogArticleCard[]) {
  const searchParams = useSearchParams();

  const [activeCategory, setActiveCategory] = useState(
    () => searchParams.get('category') ?? '',
  );
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = tabsRef.current;
    if (!el || el.clientWidth === 0) return;
    setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollLeft = 0;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  const categories = useMemo(
    () =>
      [...new Set(articles.map((a) => a.category).filter(Boolean))] as string[],
    [articles],
  );

  const filtered = useMemo(() => {
    let result = articles;
    if (activeCategory)
      result = result.filter((a) => a.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((a) =>
        portableTextToPlain(a.title).toLowerCase().includes(q),
      );
    }
    return result;
  }, [articles, activeCategory, search]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  function handleCategory(cat: string) {
    setActiveCategory(cat);
    setVisibleCount(INITIAL_COUNT);
    const params = new URLSearchParams(window.location.search);
    if (cat) params.set('category', cat);
    else params.delete('category');
    window.history.replaceState(null, '', `?${params.toString()}`);
  }

  function handleSearch(value: string) {
    setSearch(value);
    setVisibleCount(INITIAL_COUNT);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      if (value.trim()) params.set('search', value);
      else params.delete('search');
      window.history.replaceState(null, '', `?${params.toString()}`);
    }, 200);
  }

  function loadMore() {
    setVisibleCount((c) => c + INCREMENT);
  }

  function scrollTabs(dir: 'left' | 'right') {
    tabsRef.current?.scrollBy({
      left: dir === 'right' ? 220 : -220,
      behavior: 'smooth',
    });
  }

  return {
    categories,
    visible,
    hasMore,
    activeCategory,
    search,
    tabsRef,
    isOverflowing,
    handleCategory,
    handleSearch,
    loadMore,
    scrollTabs,
  };
}
